import { useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// The Deep Core (level 100) — the endless capstone. Each gate throws intrusion signatures and you
// answer with the tool that repels each one, except the core keeps rewriting the mapping: every
// couple of gates a mutation is applied and they STACK, and once the stack is full it churns, so
// the composed mapping never settles into something memorisable.
//
// Extract at any gate to bank the depth reached; push on and a wrong answer (or a window that
// runs out) locks you out for half.
//
// The base table and the mutation list come from the server, and the composed answer is never
// sent — working it out is the game. This component only renders what it was told and reports
// the picks; the server grades every gate against its own clock.

const TOOL_COLOR: Record<string, string> = {
  PasswordHashing: '#f87171',
  Firewall: '#fbbf24',
  DdosBreaker: '#60a5fa',
  AntiVirus: '#c084fc',
};

interface Mutation {
  kind: 'rotate' | 'mirror' | 'swap';
  /** For a rotate: how many places the cycle turns (1 = +1, 3 = −1). */
  step: number;
  a: string | null;
  b: string | null;
}

interface Challenge {
  depth: number;
  /** False until the player begins — the first gate has not landed yet. */
  started: boolean;
  gateNumber: number;
  openedMs: number;
  windowMs: number;
  /** The signatures this gate throws, in the order they must be answered. */
  signatures: string[];
  mutations: Mutation[];
  /** Vector → tool, before any mutation. Sent so the client can't drift from the grader. */
  baseMapping: Record<string, string>;
  patches: number;
  patchesGranted: number;
  patchNote: string | null;
  minPayingDepth: number;
  fullFactorDepth: number;
  lockoutShare: number;
}

/** Grace before we ask the server to settle a window our own clock says is gone. */
const CATCHUP_GRACE_MS = 500;

function mutationLabel(m: Mutation): string {
  if (m.kind === 'rotate') return m.step === 1 ? 'ROTATE +1' : 'ROTATE −1';
  if (m.kind === 'mirror') return 'MIRROR';
  return `SWAP ${m.a} ↔ ${m.b}`;
}

function mutationHint(m: Mutation): string {
  if (m.kind === 'rotate') {
    return m.step === 1
      ? 'step one place down the list, wrapping round'
      : 'step one place up the list, wrapping round';
  }
  if (m.kind === 'mirror') return 'the list is reversed: first ↔ last, second ↔ third';
  return 'these two tools trade places';
}

export default function DeepCoreView({
  challenge, onAct, onSubmit, setPendingSubmission, startedAgoMs, busy,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [picks, setPicks] = useState<string[]>([]);
  const [elapsed, setElapsed] = useState(0);
  const sentRef = useRef(false);
  const catchupRef = useRef<number | null>(null);

  // Anchored during render and pulled back by however long the attempt has been running, so a
  // resumed descent lines up with the server's clock — see the note in PortScanSweepView.
  const anchorRef = useRef<{ key: unknown; at: number }>({ key: challenge, at: Date.now() - startedAgoMs });
  if (anchorRef.current.key !== challenge) anchorRef.current = { key: challenge, at: Date.now() - startedAgoMs };

  useEffect(() => {
    setLive(challenge ?? null);
    setPicks([]);
    sentRef.current = false;
    catchupRef.current = null;
    // Extracting is what a submit means here, so an empty payload banks the depth reached.
    setPendingSubmission({ kind: 'extract' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]);

  useEffect(() => {
    const id = setInterval(() => setElapsed(Date.now() - anchorRef.current.at), 50);
    return () => clearInterval(id);
  }, []);

  const tools = useMemo(() => Object.values(live?.baseMapping ?? {}), [live]);
  const vectors = useMemo(() => Object.keys(live?.baseMapping ?? {}), [live]);

  const signatures = live?.signatures ?? [];
  const windowMs = live?.windowMs ?? 0;
  const openedMs = live?.openedMs ?? 0;
  const started = live?.started ?? false;
  const open = started && elapsed >= openedMs;
  const remaining = Math.max(0, openedMs + windowMs - elapsed);
  const expired = open && elapsed > openedMs + windowMs + CATCHUP_GRACE_MS;

  // A window that ran out has to be settled by the SERVER (it costs the run, or a patch), so
  // ask it to catch up — once per gate INSTANCE. Keyed on openedMs, not the gate number: a hot
  // patch re-opens the SAME gate number, so keying on the number meant the guard blocked every
  // catch-up after the first and the board froze with nothing left to press.
  useEffect(() => {
    if (!expired || busy || !live || !started) return;
    if (catchupRef.current === live.openedMs) return;
    catchupRef.current = live.openedMs;
    void (async () => {
      const next = await onAct({ kind: 'sync' });
      if (next) { setLive(next); setPicks([]); }
    })();
  }, [expired, busy, live, started, onAct]);

  const send = async (answers: string[]) => {
    if (busy || !live || sentRef.current) return;
    if (answers.length !== signatures.length) return;
    sentRef.current = true;
    const next = await onAct({ kind: 'descend', answers });
    if (next) { setLive(next); setPicks([]); }
    // Released either way: a rejected round-trip must not lock the gate out, and when the run
    // ends the host takes over the screen anyway.
    sentRef.current = false;
  };

  const pick = (tool: string) => {
    if (busy || !open || expired || sentRef.current) return;
    if (picks.length >= signatures.length) return;
    const next = [...picks, tool];
    setPicks(next);

    // A gate with ONE signature has nothing to review, so the tool you tap IS the answer.
    // Requiring a second press on a separate button was a hidden step: under a six-second
    // window players tapped the tool, waited for it to count, and the gate expired around them
    // exactly as if they had never answered. Gates with several signatures keep the explicit
    // button, because there the order is worth checking before committing.
    if (signatures.length === 1 && next.length === 1) void send(next);
  };

  const undo = () => {
    if (busy || sentRef.current) return;
    setPicks(picks.slice(0, -1));
  };

  const begin = async () => {
    if (busy || started) return;
    const next = await onAct({ kind: 'ready' });
    if (next) { setLive(next); setPicks([]); }
  };

  const depth = live?.depth ?? 0;
  const banks = depth >= (live?.minPayingDepth ?? 4);

  const cell: React.CSSProperties = {
    padding: '0.2rem 0.4rem', fontFamily: 'monospace', fontSize: '0.75rem',
  };

  return (
    <div style={{ fontFamily: 'monospace' }}>
      {/* Depth and the extract decision */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.7rem', background: '#0d1117',
      }}>
        <span style={{ fontSize: '1.4rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>
          DEPTH {depth}
        </span>
        <span style={{ fontSize: '0.74rem', color: banks ? '#4ade80' : 'var(--text-muted)' }}>
          {banks
            ? `banks ${depth >= (live?.fullFactorDepth ?? 12) ? 'the full haul' : 'a partial haul'}`
            : `nothing banked below depth ${live?.minPayingDepth ?? 4}`}
        </span>
        {(live?.patchesGranted ?? 0) > 0 && (
          <span style={{ fontSize: '0.74rem', color: (live?.patches ?? 0) > 0 ? '#60a5fa' : '#6b7280' }}>
            hot patches {live?.patches ?? 0}/{live?.patchesGranted ?? 0}
          </span>
        )}
        <button
          onClick={() => onSubmit({ kind: 'extract' })}
          disabled={busy}
          style={{ marginLeft: 'auto', fontSize: '0.78rem', padding: '0.3rem 0.8rem', color: '#4ade80', borderColor: '#166534' }}
          title="Bank the depth you have reached and end the descent"
        >
          Extract
        </button>
      </div>

      {live?.patchNote && (
        <p className="fx-fade-in" style={{ margin: '0 0 0.6rem', fontSize: '0.76rem', color: '#60a5fa' }}>
          {live.patchNote}
        </p>
      )}

      {/* The mapping: base table plus the mutations to compose over it */}
      <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.55rem 0.7rem', marginBottom: '0.7rem' }}>
        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.3rem' }}>
          base mapping
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {vectors.map((v, i) => (
            <span key={v} style={cell}>
              <span style={{ color: 'var(--text-muted)' }}>{i + 1}.</span>{' '}
              <span style={{ color: '#cbd5e1' }}>{v}</span>{' '}
              <span style={{ color: 'var(--text-muted)' }}>→</span>{' '}
              <span style={{ color: TOOL_COLOR[live?.baseMapping?.[v] ?? ''] ?? '#cbd5e1' }}>
                {live?.baseMapping?.[v]}
              </span>
            </span>
          ))}
        </div>

        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', margin: '0.5rem 0 0.3rem' }}>
          active mutations {(live?.mutations?.length ?? 0) > 0 ? '— apply in this order' : ''}
        </div>
        {(live?.mutations?.length ?? 0) === 0 ? (
          <div style={{ ...cell, color: '#4ade80' }}>none — the mapping is still clean</div>
        ) : (
          <ol style={{ margin: 0, paddingLeft: '1.3rem' }}>
            {live!.mutations.map((m, i) => (
              <li key={i} style={{ ...cell, color: '#fde047' }}>
                {mutationLabel(m)}
                <span style={{ color: 'var(--text-muted)' }}> — {mutationHint(m)}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* The gate */}
      <div style={{
        border: `1px solid ${open && !expired ? 'var(--primary-color)' : '#232833'}`,
        borderRadius: 4, padding: '0.8rem', marginBottom: '0.6rem', textAlign: 'center',
        background: open ? '#12161d' : '#0d1117',
      }}>
        {!started ? (
          <div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginBottom: '0.5rem' }}>
              Read the mapping above first — once you begin, each gate is on a clock.
            </div>
            <button
              className="primary"
              onClick={begin}
              disabled={busy}
              style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
            >
              Begin descent
            </button>
          </div>
        ) : !open ? (
          <div style={{ color: '#4b5563', fontSize: '0.9rem' }}>the next gate is opening…</div>
        ) : (
          <>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              gate {live?.gateNumber} · answer {signatures.length === 1 ? 'the signature' : `all ${signatures.length}, in order`}
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap', margin: '0.4rem 0' }}>
              {signatures.map((sig, i) => (
                <span key={i} style={{
                  fontSize: '1.05rem', fontWeight: 'bold', letterSpacing: '0.06em',
                  padding: '0.1rem 0.35rem', borderRadius: 3,
                  color: i < picks.length ? '#4ade80' : '#e2e8f0',
                  border: `1px solid ${i === picks.length ? 'var(--primary-color)' : 'transparent'}`,
                }}>
                  {sig}
                  {i < picks.length && (
                    <span style={{ color: TOOL_COLOR[picks[i]] ?? '#4ade80', fontSize: '0.72rem', fontWeight: 'normal' }}>
                      {' '}→ {picks[i]}
                    </span>
                  )}
                </span>
              ))}
            </div>
            <div style={{ height: 4, background: '#1a1a2e', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${windowMs > 0 ? Math.max(0, (remaining / windowMs) * 100) : 0}%`,
                background: remaining < windowMs * 0.25 ? '#f87171' : 'var(--primary-color)',
                transition: 'width 0.05s linear',
              }} />
            </div>
            <div style={{ color: expired ? '#f87171' : '#6b7280', fontSize: '0.72rem', marginTop: '0.3rem' }}>
              {expired ? 'the gate sealed' : `${(remaining / 1000).toFixed(1)}s`}
            </div>
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        {tools.map(t => (
          <button
            key={t}
            onClick={() => pick(t)}
            disabled={busy || !open || expired || picks.length >= signatures.length}
            style={{ fontSize: '0.76rem', padding: '0.4rem 0.7rem' }}
          >
            {t}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {signatures.length > 1 && (
          <>
            <button
              className="primary"
              onClick={() => void send(picks)}
              disabled={busy || !open || expired || picks.length !== signatures.length}
              style={{ fontSize: '0.82rem', padding: '0.35rem 0.9rem' }}
            >
              {busy ? '…' : `Descend (${picks.length}/${signatures.length})`}
            </button>
            <button onClick={undo} disabled={busy || picks.length === 0} style={{ fontSize: '0.76rem', padding: '0.3rem 0.7rem' }}>
              undo
            </button>
          </>
        )}
        <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
          {signatures.length === 1
            ? 'tap the tool — that is your answer'
            : `tap one tool per signature, in order, then descend`}
          {' · '}
          a wrong answer costs {(live?.patches ?? 0) > 0 ? 'a hot patch' : `the run — you keep ${Math.round((live?.lockoutShare ?? 0.5) * 100)}% of the haul`}
        </span>
      </div>
    </div>
  );
}
